/* Billing helpers shared by the member page, admin drawer and dashboard cards. */
import api from "@/lib/api";

export const fmtCAD = (n) =>
  `CA$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const fmtInt = (n) => Number(n || 0).toLocaleString("en-CA");

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—");

export const STATUS_META = {
  active: { label: "Active", tone: "emerald" },
  cancelling: { label: "Cancels at period end", tone: "amber" },
  complimentary: { label: "Complimentary period", tone: "sky" },
  past_due: { label: "Payment overdue", tone: "amber" },
  read_only: { label: "Read-only — payment required", tone: "slate" },
  inactive: { label: "Not activated", tone: "rose" },
  ended: { label: "Subscription ended", tone: "rose" },
  exempt: { label: "Admin — exempt", tone: "slate" },
  not_live: { label: "Billing not live yet", tone: "slate" },
};

export const TONE_CLASSES = {
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  sky: "bg-sky-50 text-sky-800 border-sky-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  rose: "bg-rose-50 text-rose-800 border-rose-200",
};

export const POOL_TONE = {
  enrichment: { ring: "#C8102E", soft: "#FDE2E6", text: "text-[#C8102E]" },
  scrape: { ring: "#0F172A", soft: "#E2E8F0", text: "text-slate-900" },
};

/* Approximate-results conversions for the pack tiles & Build-Your-Own wheels.
   Fixed in Phase 1 (admin-editable in Phase 2). */
export const RESULTS = {
  enrichment: { low: 5, high: 10, unit: "realtor enrichments (socials + photo)" }, // credits per result
  scrape: { per: 1, unit: "agent imports" },
};

/* Authoritative price lookup — uses the server-sent price_table so the preview
   matches the fixed packs and the checkout exactly; falls back to the formula. */
export function creditPrice(pricing, pool, credits) {
  const c = Number(credits) || 0;
  const table = (pricing?.price_table || {})[pool];
  if (table && table[String(c)] != null) return Number(table[String(c)]);
  const unit = (pricing?.pool_unit_usd || {})[pool] || 0;
  const fx = pricing?.fx_rate || 1.37;
  const markup = pricing?.markup_pct ?? 10;
  const cost = Math.round(c * unit * fx * 100) / 100;
  return Math.round(cost * (1 + markup / 100) * 100) / 100;
}

export function approxResults(pool, credits, ratios) {
  const c = Number(credits) || 0;
  if (c <= 0) return "";
  if (pool === "scrape") {
    const cpr = ratios?.scrape?.cpr || RESULTS.scrape.per; // credits per agent import
    return `≈ ${Math.round(c / cpr).toLocaleString("en-CA")} ${RESULTS.scrape.unit}`;
  }
  const lowCpr = ratios?.enrichment?.cpr_low || RESULTS.enrichment.low;
  const highCpr = ratios?.enrichment?.cpr_high || RESULTS.enrichment.high;
  const high = Math.round(c / lowCpr);
  const low = Math.round(c / highCpr);
  return `≈ ${low.toLocaleString("en-CA")}–${high.toLocaleString("en-CA")} ${RESULTS.enrichment.unit}`;
}

/* Plain-language "what this whole order gets you" lines for the cart. */
export function resultsSummary(creditsByPool, ratios) {
  const out = [];
  if ((creditsByPool.enrichment || 0) > 0) out.push(approxResults("enrichment", creditsByPool.enrichment, ratios).replace("≈ ", ""));
  if ((creditsByPool.scrape || 0) > 0) out.push(approxResults("scrape", creditsByPool.scrape, ratios).replace("≈ ", ""));
  return out;
}

/** Hand the browser off to Authorize.Net's hosted page (POST the token). */
export function postToHosted(hostedUrl, token) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = hostedUrl;
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = "token";
  input.value = token;
  form.appendChild(input);
  document.body.appendChild(form);
  form.submit();
}

export async function downloadReceipt(receiptId, number) {
  const res = await api.get(`/billing/receipts/${receiptId}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${number || "receipt"}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
